// TrainingSessionsLog - strukturovaný log tréninků s datem.
// 29.5.2026 v11: nahrazuje volný textarea "posledni_treninky" v profilu klienta.
//
// Features:
//   - Inline form: datum (default = dnes) + cviky textarea + Uložit
//   - Seznam grouped by rok → měsíc (auto-collapse starých měsíců)
//   - Search input fulltext napříč cviky + poznámka (server-side ILIKE)
//   - Per-záznam edit / delete (klik → expanduje editor)
//   - Mobile-first karty, žádné tabulky
//   - Optimistic updates (instant UI, API běží na pozadí)

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Plus, Search, Pencil, Trash2, Calendar, ChevronDown, ChevronRight, Loader2, User } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import ConfirmDialog from "./ConfirmDialog.jsx";
import { toast } from "./Toast.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";

const MONTH_NAMES = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];

function fmtDate(iso) {
  if (!iso) return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}. ${m[1]}`;
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// localStorage cache - instant render historických tréninků (TTL 1h)
const SESSIONS_CACHE_PREFIX = "fw40_cts_";
const SESSIONS_CACHE_TTL_MS = 60 * 60 * 1000;
function readSessionsCache(klientId) {
  if (!klientId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SESSIONS_CACHE_PREFIX + klientId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - (parsed._ts || 0) > SESSIONS_CACHE_TTL_MS) return null;
    return parsed.sessions;
  } catch { return null; }
}
function writeSessionsCache(klientId, sessions) {
  if (!klientId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SESSIONS_CACHE_PREFIX + klientId, JSON.stringify({ _ts: Date.now(), sessions }));
  } catch { /* quota */ }
}

export default function TrainingSessionsLog({ klientName, canEdit = false, onCount }) {
  const klientId = String(klientName || "").toLowerCase().trim();
  // 29.5.2026 v14: smazat trénink může jen owner / manager. Trenér jen Upravit.
  const currentUserRole = String(getCurrentUser()?.role || "").toLowerCase();
  const canDelete = canEdit && (currentUserRole === "owner" || currentUserRole === "manager");
  const initialCached = readSessionsCache(klientId) || [];
  const [sessions, setSessions] = useState(initialCached);
  const [loading, setLoading] = useState(initialCached.length === 0);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newDatum, setNewDatum] = useState(todayIso());
  const [newCviky, setNewCviky] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [openMonths, setOpenMonths] = useState({}); // klíč "2026-05" → bool. Default: aktuální měsíc open, starší collapsed.
  // ConfirmDialog místo native confirm() — 29.5.2026 v15.
  const [confirmDeleteSession, setConfirmDeleteSession] = useState(null);
  // 9.9.2026 — nadrazena podsekce si chce ukazat pocet zapisu v hlavicce.
  useEffect(() => { if (typeof onCount === "function") onCount(sessions.length); }, [sessions.length, onCount]); // {id, datum, cviky} nebo null
  const searchDebounceRef = useRef(null);

  // Debounce search input → query (server fetch)
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setQuery(searchInput.trim()), 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!klientId) return;
    // Silent refresh pokud máme cache, jinak ukaž loading
    if (!query && !readSessionsCache(klientId)) setLoading(true);
    setError(null);
    try {
      const url = `/api/client-training-sessions?klient_id=${encodeURIComponent(klientId)}${query ? `&q=${encodeURIComponent(query)}` : ""}`;
      const r = await apiFetch(url, { credentials: "include" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Načtení selhalo");
      setSessions(j.sessions || []);
      // Cache jen full list (bez query filtru)
      if (!query) writeSessionsCache(klientId, j.sessions || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [klientId, query]);

  useEffect(() => { load(); }, [load]);

  // Group sessions by year+month → seřazené desc (nejnovější rok+měsíc nahoře).
  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of sessions) {
      const key = s.datum?.substring(0, 7) || "0000-00"; // YYYY-MM
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [sessions]);

  // Auto-open aktuální měsíc při prvním loadu (i po search)
  useEffect(() => {
    if (grouped.length === 0) return;
    setOpenMonths((prev) => {
      // Pokud user už něco nastavil, neforce override. Jen pokud je úplně prázdný stav, otevři první.
      if (Object.keys(prev).length > 0) return prev;
      return { [grouped[0][0]]: true };
    });
  }, [grouped]);

  const handleAdd = async () => {
    if (!newCviky.trim()) return;
    setSaving(true);
    try {
      const r = await apiFetch("/api/client-training-sessions", {
        method: "POST",
        body: JSON.stringify({ klient_id: klientId, klient_name: klientName, datum: newDatum, cviky: newCviky.trim() }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      // Optimistic add (front of list) + cache update
      setSessions((arr) => {
        const next = [j.session, ...arr];
        writeSessionsCache(klientId, next);
        return next;
      });
      // Reset form
      setNewCviky("");
      setNewDatum(todayIso());
      setShowAdd(false);
      // Otevři měsíc v listu pokud zavřený
      const monthKey = j.session.datum.substring(0, 7);
      setOpenMonths((prev) => ({ ...prev, [monthKey]: true }));
    } catch (e) {
      toast(`Chyba: ${e.message}`, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, fields) => {
    const prev = sessions;
    // Optimistic update lokálně
    setSessions((arr) => arr.map((s) => s.id === id ? { ...s, ...fields } : s));
    try {
      const r = await apiFetch("/api/client-training-sessions", {
        method: "POST",
        body: JSON.stringify({ action: "update", id, fields }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Update selhal");
      if (j.session) setSessions((arr) => {
        const next = arr.map((s) => s.id === id ? j.session : s);
        writeSessionsCache(klientId, next);
        return next;
      });
    } catch (e) {
      setSessions(prev);
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  const handleDelete = async (id) => {
    const prev = sessions;
    setSessions((arr) => {
      const next = arr.filter((s) => s.id !== id);
      writeSessionsCache(klientId, next);
      return next;
    });
    try {
      const r = await apiFetch("/api/client-training-sessions", {
        method: "POST",
        body: JSON.stringify({ action: "delete", id }),
      });
      if (!r.ok) throw new Error("Smazání selhalo");
    } catch (e) {
      setSessions(prev);
      writeSessionsCache(klientId, prev);
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  return (
    <div className="space-y-3">
      {/* Přidat trénink - full-width dashed CTA stejný styl jako "Přidat cvik" */}
      {canEdit && !showAdd && (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="w-full inline-flex items-center justify-center gap-2 text-base font-bold px-4 py-3 border-2 border-dashed border-slate-300 hover:border-brand-400 hover:bg-brand-50 rounded-xl text-slate-600 hover:text-brand-700 transition-colors"
        >
          <Plus className="w-5 h-5" /> Přidat trénink
        </button>
      )}

      {/* Inline add form */}
      {showAdd && canEdit && (
        <div className="bg-brand-50 border-2 border-brand-200 rounded-xl p-3 sm:p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Calendar className="w-4 h-4 text-brand-600 flex-shrink-0" />
            <input
              type="date"
              value={newDatum}
              onChange={(e) => setNewDatum(e.target.value)}
              className="field w-auto sm:text-base font-bold"
            />
          </div>
          <textarea
            value={newCviky}
            onChange={(e) => setNewCviky(e.target.value)}
            placeholder="Co se cvičilo: Veslo + zvíře, TGU, výpady, záda kladka, plank..."
            rows={3}
            autoFocus
            className="field w-full sm:text-sm resize-y"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={handleAdd}
              disabled={saving || !newCviky.trim()}
              className="btn btn-primary flex-1"
            >
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Ukládám…</> : <>Uložit trénink</>}
            </button>
          </div>
        </div>
      )}

      {/* Drobný status řádek - jen total počet */}
      {!loading && sessions.length > 0 && (
        <div className="text-xs text-slate-500">
          <b className="text-slate-700">{sessions.length}</b> {sessions.length === 1 ? "trénink celkem" : sessions.length < 5 ? "tréninky celkem" : "tréninků celkem"}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 italic p-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Načítám tréninky…
        </div>
      ) : error ? (
        <div className="text-sm text-rose-700 p-3 bg-rose-50 border border-rose-200 rounded-xl">Chyba: {error}</div>
      ) : grouped.length === 0 ? (
        <div className="text-sm text-slate-400 italic p-4 text-center">
          {query ? `Žádný trénink neobsahuje "${query}".` : "Žádné tréninky zatím. Klikni Přidat trénink."}
        </div>
      ) : (
        <div className="space-y-2">
          {grouped.map(([monthKey, items]) => {
            const [year, month] = monthKey.split("-");
            const isOpen = !!openMonths[monthKey];
            const totalCount = items.length;
            return (
              <div key={monthKey} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenMonths((p) => ({ ...p, [monthKey]: !isOpen }))}
                  className="w-full px-3 sm:px-4 py-2.5 flex items-center gap-2 hover:bg-slate-50 transition-colors"
                >
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <span className="text-sm sm:text-base font-bold text-slate-800 capitalize">{MONTH_NAMES[parseInt(month, 10) - 1]} {year}</span>
                  <span className="text-xs text-slate-500">{totalCount} {totalCount === 1 ? "trénink" : totalCount < 5 ? "tréninky" : "tréninků"}</span>
                </button>
                {isOpen && (
                  <div className="divide-y divide-slate-100">
                    {items.map((s) => (
                      <SessionCard
                        key={s.id}
                        session={s}
                        canEdit={canEdit}
                        canDelete={canDelete}
                        editing={editingId === s.id}
                        query={query}
                        onStartEdit={() => setEditingId(s.id)}
                        onCancelEdit={() => setEditingId(null)}
                        onUpdate={(fields) => { handleUpdate(s.id, fields); setEditingId(null); }}
                        onDelete={() => setConfirmDeleteSession(s)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Styled confirm dialog místo native confirm() pro smazání */}
      <ConfirmDialog
        open={!!confirmDeleteSession}
        title="Smazat trénink?"
        description={confirmDeleteSession ? (
          <div className="mt-2 space-y-2">
            <div className="text-sm text-slate-600">Tato akce je nevratná.</div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 space-y-1">
              <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {fmtDate(confirmDeleteSession.datum)}, {weekday(confirmDeleteSession.datum)}
                {confirmDeleteSession.trener_name && <span className="ml-2 inline-flex items-center gap-1"><User className="w-3 h-3" /> {confirmDeleteSession.trener_name}</span>}
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap break-words line-clamp-3">{confirmDeleteSession.cviky}</div>
            </div>
          </div>
        ) : null}
        confirmLabel="Smazat"
        cancelLabel="Zrušit"
        variant="danger"
        onConfirm={async () => {
          const s = confirmDeleteSession;
          setConfirmDeleteSession(null);
          if (s) await handleDelete(s.id);
        }}
        onCancel={() => setConfirmDeleteSession(null)}
      />
    </div>
  );
}

// === SessionCard - karta jednoho tréninku ===
// Default: jen header (datum + trenér pillboxy). Klik → expand detail s cviky.
function SessionCard({ session, canEdit, canDelete, editing, query, onStartEdit, onCancelEdit, onUpdate, onDelete }) {
  const [draftDatum, setDraftDatum] = useState(session.datum);
  const [draftCviky, setDraftCviky] = useState(session.cviky);
  const [expanded, setExpanded] = useState(false);
  const uzka = useUzkaObrazovka();
  useEffect(() => { setDraftDatum(session.datum); setDraftCviky(session.cviky); }, [session.datum, session.cviky, editing]);
  // Auto-expand pokud editing
  useEffect(() => { if (editing) setExpanded(true); }, [editing]);

  if (editing) {
    return (
      <div className="bg-brand-50/40 p-3 sm:p-4 space-y-2 border-l-4 border-brand-400">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-brand-600" />
          <input
            type="date"
            value={draftDatum}
            onChange={(e) => setDraftDatum(e.target.value)}
            className="field w-auto font-bold"
          />
        </div>
        <textarea
          value={draftCviky}
          onChange={(e) => setDraftCviky(e.target.value)}
          rows={3}
          className="field w-full sm:text-sm resize-y"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={() => onUpdate({ datum: draftDatum, cviky: draftCviky.trim() })}
            disabled={!draftCviky.trim()}
            className="btn btn-primary flex-1"
          >
            Uložit
          </button>
          <button
            onClick={onCancelEdit}
            className="btn btn-secondary"
          >
            Zrušit
          </button>
          {/* Smazat: jen owner/manager. Trenér nesmí. */}
          {canDelete && (
            <button
              onClick={onDelete}
              title="Smazat trénink"
              className="px-3 py-2 rounded-xl bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 min-h-[44px] sm:min-h-0"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Read mode: header s pill boxy (datum + trenér) a hned vedle akce (Upravit/Smazat).
  // Klik na pill boxy / chevron expanduje detail s cviky textem.
  const highlighted = highlightQuery(session.cviky, query);
  // 9.9.2026 — na telefonu jsou akce pod datem jako tri tlacitka pres celou sirku,
  // kazde vysoke 44 px. Drive byly tri male ikony vedle sebe bez odstupu a tuzka
  // sousedila s kosem. Na pocitaci zustava puvodni radek s ikonami vpravo.
  const pilulky = (
    <>
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-800">
        <Calendar className="h-3 w-3" />
        {fmtDate(session.datum)} <span className="font-normal opacity-60">, {weekday(session.datum)}</span>
      </span>
      {session.trener_name && (
        <span className="inline-flex items-center gap-1 rounded-full border border-brand-200 bg-brand-50 px-2 py-1 text-xs font-bold text-brand-800">
          <User className="h-3 w-3" />
          {session.trener_name}
        </span>
      )}
    </>
  );
  const detail = expanded && (
    <div className="border-t border-slate-100 bg-slate-50/30 px-3 pb-3 sm:px-4">
      <div className="mt-3 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700 sm:text-[15px]">
        {highlighted}
      </div>
    </div>
  );

  if (uzka) {
    return (
      <div className="group">
        <div className="px-3 py-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mb-2 flex w-full flex-wrap items-center gap-1.5 text-left"
          >
            {pilulky}
          </button>
          <div className="flex items-center gap-2">
            {canEdit && (
              <button
                onClick={onStartEdit}
                title="Upravit"
                className="flex h-11 flex-1 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
            {canDelete && (
              <button
                onClick={onDelete}
                title="Smazat trénink"
                className="flex h-11 flex-1 items-center justify-center rounded-xl border border-rose-200 bg-white text-rose-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              title={expanded ? "Sbalit detail" : "Zobrazit detail"}
              className="flex h-11 flex-1 items-center justify-center rounded-xl border border-brand-200 bg-brand-50 text-brand-700"
            >
              <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
            </button>
          </div>
        </div>
        {detail}
      </div>
    );
  }

  return (
    <div className="group">
      <div className="flex items-center justify-between gap-2 px-3 py-3 transition-colors hover:bg-slate-50/50 sm:px-4">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-left"
        >
          {pilulky}
        </button>
        <div className="flex flex-shrink-0 items-center gap-1">
          {canEdit && (
            <button
              onClick={onStartEdit}
              title="Upravit"
              className="rounded-xl p-1.5 text-slate-400 hover:bg-brand-100 hover:text-brand-700 min-h-[44px] sm:min-h-0"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={onDelete}
              title="Smazat trénink"
              className="rounded-xl p-1.5 text-slate-400 hover:bg-rose-100 hover:text-rose-700 min-h-[44px] sm:min-h-0"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? "Sbalit detail" : "Zobrazit detail"}
            className="rounded-xl p-1.5 text-slate-400 hover:bg-slate-100"
          >
            <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
          </button>
        </div>
      </div>
      {detail}
    </div>
  );
}

const WEEKDAYS = ["ne", "po", "út", "st", "čt", "pá", "so"];
function weekday(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return WEEKDAYS[d.getDay()] || "";
  } catch { return ""; }
}

// Wrap query matches in <mark> pro vizualizaci search
function highlightQuery(text, query) {
  if (!query || !text) return text;
  const norm = (s) => String(s).toLowerCase();
  const q = norm(query);
  const lower = norm(text);
  const idx = lower.indexOf(q);
  if (idx === -1) return text;
  const parts = [];
  let cursor = 0;
  let nextIdx = idx;
  while (nextIdx !== -1) {
    parts.push(text.substring(cursor, nextIdx));
    parts.push(<mark key={nextIdx} className="bg-amber-200 text-amber-900 rounded px-0.5">{text.substring(nextIdx, nextIdx + q.length)}</mark>);
    cursor = nextIdx + q.length;
    nextIdx = lower.indexOf(q, cursor);
  }
  parts.push(text.substring(cursor));
  return parts;
}
